import React, {useReducer, useState} from 'react'
import {makeStyles, TableCell, TableRow} from '@material-ui/core';
import PropTypes from 'prop-types';
import Tr from 'translations/Translations';
import moment from "libs/moment";
import WorkableIconDownloadButton from 'components/button/WorkableIconDownloadButton';
import WorkableIconUnpublishButton from "components/button/WorkableIconUnpublishButton";
import WorkableIconPublishButton from "components/button/WorkableIconPublishButton";
import WorkableIconDownloadCountButton from "components/button/WorkableIconDownloadCountButton";
import DateFormats from "constants/DateFormats";
import iconWorksReducer from "libs/iconWorksReducer";
import SiwzService from "services/SiwzService";
import WorkableIconSearchButton from "components/button/WorkableIconSearchButton";
import {getPublishDateCellValue} from "./SiwzFileTableRow";
import clsx from "clsx";
import WorkableIconShareButton from "components/button/WorkableIconShareButton";
import useSnackbar from "hooks/useSnackbar";
import ShareSelectPopup, {sharePopupData} from "components/popup/ShareSelectPopup";

const useStyle = makeStyles(theme => ({
    cell: {
        padding: theme.spacing(1),
    },
    buttonColumn: theme.components.table.buttonColumn
}));

function SiwzVersionsTableRow(
    {
        demandId,
        rootRowIdentity,
        row,
        onOpenDownloadHistory,
        onEditDescriptionFile,
        showShare,
    }) {

    const classes = useStyle();
    const [shareFilePopup, setShareFilePopup] = useState(null);
    const {enqueueSuccessSnackbar} = useSnackbar();
    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        preview: false,
        download: false,
        publish: false,
        share: false,
        downloadCount: false
    });

    const onDownload = () => {
        dispatchIconWorks({type: 'download', works: true});
        SiwzService.getDownloadFile(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source, rootRowIdentity), false)
            .finally(() => dispatchIconWorks({type: 'download', works: false}));
    }

    const onPublish = () => {
        dispatchIconWorks({type: 'publish', works: true});
        SiwzService.publish(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source, rootRowIdentity))
            .finally(() => dispatchIconWorks({type: 'publish', works: false}));
    };

    const onUnpublish = () => {
        dispatchIconWorks({type: 'publish', works: true});
        SiwzService.unpublish(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source, rootRowIdentity))
            .finally(() => dispatchIconWorks({type: 'publish', works: false}));
    };

    const onShareFile = (row) => setShareFilePopup(sharePopupData(row.name, row));

    const doShare = (row, system) => {
        setShareFilePopup(null);
        dispatchIconWorks({type: 'share', works: true});
        SiwzService.shareSiwz(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source, rootRowIdentity), system).then((resp) => {
            if (resp)
                enqueueSuccessSnackbar(Tr("int_upload_success_x", {x: system}, true))
        }).finally(() => {
            dispatchIconWorks({type: 'share', works: false})
        });
    }

    return (<TableRow>
        <TableCell className={classes.cell}>{row.name}</TableCell>
        <TableCell className={clsx(classes.cell, classes.buttonColumn)}>
            <WorkableIconSearchButton
                onClick={() => onEditDescriptionFile(row, rootRowIdentity)}
                tooltip={row.description ? <p className={classes.descriptionTooltip}>{row.description}</p> : <i>{Tr('no_description')}</i>}
                disabled={iconWorks.anyWorks}
            />
        </TableCell>
        <TableCell className={classes.cell}>{row.typeTranslated}</TableCell>
        <TableCell className={classes.cell}>{row.version}</TableCell>
        <TableCell className={classes.cell}>{moment(row.createDate).format(DateFormats.DATETIME_FORMAT)}</TableCell>
        <TableCell className={classes.cell}>{getPublishDateCellValue(row)}</TableCell>
        <TableCell className={clsx(classes.cell, classes.buttonColumn)}>
            <WorkableIconDownloadButton
                onClick={() => onDownload(row)}
                disabled={iconWorks.anyWorks || !row.actions.download}
                hideTooltip={!row.actions.download}
                working={iconWorks.download}
            />
        </TableCell>
        {showShare && <TableCell className={clsx(classes.cell, classes.buttonColumn)}>
            <WorkableIconShareButton
                onClick={() => onShareFile(row)}
                disabled={iconWorks.anyWorks || !row.actions.download}
                working={iconWorks.share}
            />
            {!!shareFilePopup && <ShareSelectPopup
                data={shareFilePopup}
                onCancel={() => setShareFilePopup(null)}
                onChoose={doShare}
            />}
        </TableCell>}
        <TableCell className={clsx(classes.cell, classes.buttonColumn)}>
            {!!row.actions.unpublish ?
                <WorkableIconUnpublishButton
                    onClick={() => onUnpublish(row)}
                    disabled={iconWorks.anyWorks || !row.actions.unpublish}
                    working={iconWorks.publish}
                /> :
                <WorkableIconPublishButton
                    onClick={() => onPublish(row)}
                    disabled={iconWorks.anyWorks || !row.actions.publish}
                    hideTooltip={!row.actions.publish}
                    working={iconWorks.publish}
                />
            }
        </TableCell>
        <TableCell className={clsx(classes.cell, classes.buttonColumn)}>
            <WorkableIconDownloadCountButton
                tooltip={Tr("download_count_x", {downloadCount: row.downloadCount})}
                onClick={() => onOpenDownloadHistory(row, rootRowIdentity)}
                disabled={!row.actions.downloadDetails}
                hideTooltip={!row.actions.downloadDetails}
            />
        </TableCell>
    </TableRow>)
}

SiwzVersionsTableRow.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    rootRowIdentity: PropTypes.number.isRequired,
    row: PropTypes.shape({
        identity: PropTypes.number.isRequired,
        source: PropTypes.string.isRequired
    }).isRequired,
    onOpenDownloadHistory: PropTypes.func.isRequired,
    onEditDescriptionFile: PropTypes.func.isRequired,
    showShare: PropTypes.bool.isRequired
};

export default SiwzVersionsTableRow;